import type { CustomerUpcomingAction } from "@repo/app-types/entities/performanceCustomer";
import type { CustomerTimelineRow } from "@repo/ui/customerTimeline/customerTimeline";
import { CustomerTimelineActivityDetails } from "@repo/ui/customerTimeline/customerTimelineActivityDetails";
import {
  CustomerTimelineDotNode,
  CustomerTimelineIconNode,
} from "@repo/ui/customerTimeline/customerTimelineNodes";
import { CustomerTimelineRowDetails } from "@repo/ui/customerTimeline/customerTimelineRowDetails";
import { Size } from "@repo/ui/helpers/helpers";
import { TeammateLabel } from "@repo/ui/teammate/teammateLabel";
import { IconPhoneOutgoing } from "@tabler/icons-react";
import { DateTime } from "luxon";
import { PH_MASK_CLASS } from "../../analytics/sessionRecordingMasking.js";
import {
  buildCustomerTimelineParticipant,
  buildCustomerTimelineTeammateLabel,
  formatCustomerTimelineTime,
  type OpenCustomerInteraction,
} from "./timelineRowHelpers.js";

export function buildUpcomingActionRowId(
  action: CustomerUpcomingAction,
): string {
  return `upcoming:${action.grading_id}`;
}

/** A dialer's scheduled call. */
export function buildUpcomingActionRow(
  action: CustomerUpcomingAction,
  openInteraction: OpenCustomerInteraction,
): CustomerTimelineRow {
  const { next_action: nextAction } = action;

  return {
    id: buildUpcomingActionRowId(action),
    dateTime: nextAction.scheduled_at,
    dateLabel: formatCustomerTimelineTime(nextAction.scheduled_at),
    node: (
      <CustomerTimelineIconNode
        icon={<IconPhoneOutgoing className="size-4" />}
        isUpcoming
      />
    ),
    details: (
      <div className={PH_MASK_CLASS}>
        <CustomerTimelineRowDetails
          title="Call scheduled"
          participants={[buildCustomerTimelineParticipant(action.teammate)]}
          onOpen={() => openInteraction(action.grading_id)}
        />
      </div>
    ),
  };
}

/**
 * An agent waiting on the customer until its next turn, as a live row. It
 * happens now, so it shows no time.
 */
export function buildWaitingRow(
  action: CustomerUpcomingAction,
): CustomerTimelineRow {
  const timeLabel = formatCustomerTimelineTime(action.next_action.scheduled_at);
  return {
    id: buildUpcomingActionRowId(action),
    dateTime: DateTime.now().toISO(),
    node: <CustomerTimelineDotNode isLive />,
    details: (
      <CustomerTimelineActivityDetails
        info={`If there is no activity by ${timeLabel}, the agent will re-assess and decide next steps.`}
      >
        <span className={PH_MASK_CLASS}>
          <TeammateLabel
            {...buildCustomerTimelineTeammateLabel(action.teammate)}
            size={Size.SMALL}
          />
        </span>
        is waiting for activity
      </CustomerTimelineActivityDetails>
    ),
  };
}
